import { useCallback, useEffect, useRef, useState } from "react";

export interface DelayWithProgressOptions {
  minDelay?: number; // default: 2000ms
  tickInterval?: number; // default: 50ms
  easing?: "linear" | "easeInOut" | "easeOut" | "easeIn";
}

const easingFunctions = {
  linear: (t: number) => t,
  easeInOut: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  easeOut: (t: number) => t * (2 - t),
  easeIn: (t: number) => t * t,
};

export function useDelayWithProgress(options: DelayWithProgressOptions = {}) {
  const { minDelay = 2000, tickInterval = 50, easing = "linear" } = options;

  const [progress, setProgress] = useState(0);
  const [isDelaying, setIsDelaying] = useState(false);

  // Use refs to track current operation
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const operationIdRef = useRef<number>(0);
  const finalStepRef = useRef<number>(95); // Random final step between 91-100

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const reset = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setProgress(0);
    setIsDelaying(false);
    startTimeRef.current = 0;
  }, []);

  const wrapWithDelay = useCallback(
    async <T>(fn: () => Promise<T>): Promise<T> => {
      // Generate unique operation ID to prevent race conditions
      operationIdRef.current += 1;
      const currentOperationId = operationIdRef.current;

      reset();
      setIsDelaying(true);
      setProgress(0);
      startTimeRef.current = Date.now();

      // Generate random final step between 91-100
      finalStepRef.current = 91 + Math.floor(Math.random() * 10);

      const easeFn = easingFunctions[easing];
      let apiResult: T | null = null;
      let apiError: Error | null = null;
      let apiFinished = false;

      // Start API call
      const apiPromise = fn()
        .then((result) => {
          apiResult = result;
          apiFinished = true;
        })
        .catch((error) => {
          apiError = error;
          apiFinished = true;
        });

      // Start progress animation
      intervalRef.current = setInterval(() => {
        // Safety check: stop if this operation was superseded
        if (currentOperationId !== operationIdRef.current) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return;
        }

        const elapsed = Date.now() - startTimeRef.current;
        const rawProgress = Math.min(elapsed / minDelay, 1);
        const easedProgress = easeFn(rawProgress);
        const progressPercent = Math.floor(easedProgress * 100);

        setProgress(progressPercent);

        // Stop interval when we hit 100% or API finishes and reaches random final step
        if (
          progressPercent >= 100 ||
          (apiFinished && progressPercent >= finalStepRef.current)
        ) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }, tickInterval);

      // Wait for both API and min delay
      await Promise.all([
        apiPromise,
        new Promise((resolve) => setTimeout(resolve, minDelay)),
      ]);

      // Check if operation was superseded
      if (currentOperationId !== operationIdRef.current) {
        throw new Error("Operation cancelled");
      }

      setProgress(100);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // Show complete owl for 500ms
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Final check before updating state
      if (currentOperationId === operationIdRef.current) {
        setIsDelaying(false);
        setProgress(0);
      }

      if (apiError) throw apiError;
      return apiResult as T;
    },
    [minDelay, tickInterval, easing, reset]
  );

  return { progress, isDelaying, wrapWithDelay, reset };
}
